import React, { useState } from "react";
import {
  Air,
  Close,
  LocationOn,
  Refresh,
  Thermostat,
  WbSunny,
} from "@mui/icons-material";
import "./WeatherPanel.css";
import { logWeatherUse } from "../utils/eventLogger";

const weatherLabels = {
  0: "Clear sky",
  1: "Mostly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Foggy",
  48: "Foggy",
  51: "Light drizzle",
  53: "Drizzle",
  55: "Heavy drizzle",
  61: "Light rain",
  63: "Rain",
  65: "Heavy rain",
  71: "Light snow",
  73: "Snow",
  75: "Heavy snow",
  80: "Rain showers",
  81: "Rain showers",
  82: "Heavy showers",
  95: "Thunderstorm",
};

const getCurrentPosition = () =>
  new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Location is unavailable in this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      maximumAge: 300000,
      timeout: 10000,
    });
  });

const WeatherPanel = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState("");

  const loadWeather = async () => {
    setLoading(true);
    setError("");

    try {
      const position = await getCurrentPosition();
      const query = new URLSearchParams({
        latitude: String(position.coords.latitude),
        longitude: String(position.coords.longitude),
        current: "temperature_2m,apparent_temperature,weather_code,wind_speed_10m",
        temperature_unit: "fahrenheit",
        wind_speed_unit: "mph",
        timezone: "auto",
      });
      const response = await fetch(`https://api.open-meteo.com/v1/forecast?${query.toString()}`);
      if (!response.ok) throw new Error("Weather service is unavailable.");
      const data = await response.json();
      const current = data.current;
      const nextWeather = {
        temperature: Math.round(current.temperature_2m),
        feelsLike: Math.round(current.apparent_temperature),
        wind: Math.round(current.wind_speed_10m),
        condition: weatherLabels[current.weather_code] || "Local conditions",
        timezone: data.timezone_abbreviation || "Local time",
      };
      setWeather(nextWeather);
      logWeatherUse(nextWeather);
    } catch (requestError) {
      if (requestError?.code === 1) {
        setError("Location permission is needed to show local weather.");
      } else {
        setError(requestError.message || "Weather could not be loaded right now.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="weather-control">
      <button
        type="button"
        className="weather-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-label="Open weather"
        aria-expanded={open}
        title="Weather"
      >
        <WbSunny fontSize="small" />
        <span>Weather</span>
      </button>

      {open && (
        <aside className="weather-panel" aria-label="Weather on Planet Earth">
          <div className="weather-panel-heading">
            <div>
              <span>Planet Earth</span>
              <strong>Local weather</strong>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close weather">
              <Close fontSize="small" />
            </button>
          </div>

          {weather ? (
            <div className="weather-reading">
              <div className="weather-temperature">
                <strong>{weather.temperature}°</strong>
                <span>{weather.condition}</span>
              </div>
              <div className="weather-details">
                <span><Thermostat fontSize="inherit" /> Feels {weather.feelsLike}°</span>
                <span><Air fontSize="inherit" /> {weather.wind} mph</span>
                <span><LocationOn fontSize="inherit" /> {weather.timezone}</span>
              </div>
              <button type="button" className="weather-refresh" onClick={loadWeather} disabled={loading}>
                <Refresh fontSize="inherit" /> Refresh
              </button>
            </div>
          ) : (
            <div className="weather-empty">
              <p>{error || "See the conditions where you are, only when you choose to share your location."}</p>
              <button type="button" onClick={loadWeather} disabled={loading}>
                <LocationOn fontSize="inherit" /> {loading ? "Finding you" : "Use my location"}
              </button>
            </div>
          )}
        </aside>
      )}
    </div>
  );
};

export default WeatherPanel;
